/**
 * 助手消息气泡内的 TTS 播放入口（T10）。
 *
 * 按钮与 `VoiceController` 的播放状态机联动，呈现「空闲 / 加载中 / 播放中 / 失败」
 * 四态；点击空闲态开始朗读，播放中点击停止，失败态点击重试。动效引用
 * `src/design/motion.ts` 的按压预设，视觉取值全部来自设计令牌。
 */

import { motion } from 'framer-motion';
import type { ReactElement } from 'react';

import { useMotionPresets } from '../../design/motion';
import { buildComponentMeta, useComponentMeta } from '../../ui';
import type { BoxLayout, ComponentMetaInput } from '../../ui';
import { useVoice } from './context';
import styles from './voice.module.css';

export interface AssistantTtsButtonProps {
  /** 该条助手消息 id，用于与播放状态机对齐 / 作为登记 id。 */
  messageId: string;
  /** 要朗读的正文。 */
  text: string;
  /** 声明布局（390x844 画布坐标），由气泡布局计算得出。 */
  layout: BoxLayout;
  /** 组件清单里的页面标识。 */
  page?: string;
}

interface ButtonFace {
  glyph: string;
  aria: string;
}

function faceFor(state: 'idle' | 'loading' | 'playing' | 'error'): ButtonFace {
  switch (state) {
    case 'loading':
      return { glyph: '⏳', aria: '语音加载中' };
    case 'playing':
      return { glyph: '⏹', aria: '停止播放' };
    case 'error':
      return { glyph: '🔁', aria: '重试播放语音' };
    case 'idle':
      return { glyph: '🔊', aria: '播放语音' };
  }
}

export function AssistantTtsButton({
  messageId,
  text,
  layout,
  page = 'chat',
}: AssistantTtsButtonProps): ReactElement {
  const presets = useMotionPresets();
  const { controller, snapshot } = useVoice();

  const isCurrent = snapshot.playback.messageId === messageId;
  const state = isCurrent ? snapshot.playback.state : 'idle';
  const face = faceFor(state);

  const meta: ComponentMetaInput = {
    id: `chat.message.assistant.${messageId}.ttsButton`,
    page,
    role: 'button',
    label: '播放助手语音',
    layout,
    zIndex: 6,
    props: ['state'],
    notes: '助手气泡内右下角；四态联动，安卓用 ImageButton',
  };
  const ref = useComponentMeta<HTMLButtonElement>(buildComponentMeta(meta, 'button'));

  return (
    <motion.button
      ref={ref}
      type="button"
      className={styles.playButton}
      data-ui-id={meta.id}
      data-ui-role="button"
      data-state={state}
      aria-label={face.aria}
      title={face.aria}
      whileTap={presets.tap.whileTap}
      transition={presets.tap.transition}
      onClick={() => {
        if (state === 'playing' || state === 'loading') {
          controller.stopPlayback();
          return;
        }
        void controller.playMessage(messageId, { text });
      }}
    >
      <span aria-hidden="true">{face.glyph}</span>
    </motion.button>
  );
}